import React, { useEffect } from "react";
import { BrowserRouter } from "react-router-dom";
import { observer } from "mobx-react-lite";
import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider } from "@mui/material/styles";
import MessageProvider from "./stores/messages/messageContext";
import UploadProvider from "./stores/upload/uploadContext";
import UserProvider from "./stores/users/userContext";
import DataProvider from "./stores/data/dataContext";
import FilterProvider from "./stores/filters/filterContext";
import Message from "./stores/messages/Message";
import HealthBanner from "./components/health/HealthBanner";
import DevBranchBadge from "./components/health/DevBranchBadge";
import { CheckVersion } from "./stores/messages/checkversion";
import createCustomTheme from "./components/theme/createCustom";
import Router from "./components/router/router";
import { csrfUrl } from "./components/constants";
import { isNativeApp } from "./components/mobile/native";
import "./index.css";

const theme = createCustomTheme("dark");

const AppContent = observer(() => {
  return (
    <>
      <HealthBanner />
      <DevBranchBadge />
      <Message />
      <Router />
    </>
  );
});

const App = () => {
  // Fetch CSRF token on app load
  useEffect(() => {
    // Skip the auth/session bootstrap when this app instance is running INSIDE an iframe
    // (e.g. the embedded live editor on the home page). A second same-origin app instance
    // would otherwise overwrite the parent tab's CSRF cookie and log the real user out.
    if (typeof window !== "undefined" && window.self !== window.top) return;
    // The phone app has no backend session in this build — nothing to bootstrap.
    if (isNativeApp()) return;
    fetch(csrfUrl, { credentials: "include" })
      .then((res) => res.json())
      .then((data) => {
        // Frontend and backend live on different domains, so the backend's
        // csrftoken cookie isn't readable by JS here. The endpoint also returns
        // the token in its body — mirror it into a cookie on THIS domain so
        // getCookie("csrftoken") works for the X-CSRFToken header on login.
        if (data && data.csrfToken) {
          document.cookie = `csrftoken=${data.csrfToken}; path=/; SameSite=Lax`;
        }
        console.log("[App Init] CSRF token initialized");
      })
      .catch((err) =>
        console.error("[App Init] Failed to fetch CSRF token:", err),
      );
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <DataProvider>
        <UploadProvider>
        <MessageProvider>
          <CheckVersion />
          <UserProvider>
            <FilterProvider>
              <BrowserRouter>
                <AppContent />
              </BrowserRouter>
            </FilterProvider>
          </UserProvider>
        </MessageProvider>
        </UploadProvider>
      </DataProvider>
    </ThemeProvider>
  );
};

export default observer(App);
